import { ShieldAlert, CheckCircle, XCircle, Code2, ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";
import type { ApprovalRequest } from "../types";

interface ApprovalModalProps {
  request: ApprovalRequest;
  onApprove: (feedback?: string) => void;
  onReject: (feedback?: string) => void;
}

export default function ApprovalModal({ request, onApprove, onReject }: ApprovalModalProps) {
  const [feedback, setFeedback] = useState("");
  const [showDetails, setShowDetails] = useState(false);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in">
      <div className="w-full max-w-lg rounded-2xl border border-amber-800/40 bg-dark-surface overflow-hidden shadow-[0_0_60px_rgba(245,158,11,0.15)] animate-slide-up">

        {/* Top warning bar */}
        <div className="h-[2px] bg-gradient-to-r from-amber-500 via-cf-orange to-transparent" />

        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-dark-border bg-amber-950/20">
          <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-amber-500/10 border border-amber-500/20 flex-shrink-0">
            <ShieldAlert className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-slate-200 tracking-wide">Human-in-the-Loop Authorization</h2>
            <p className="text-xs font-mono text-slate-500">Agent paused — awaiting your decision</p>
          </div>
          {/* Pulsing dot */}
          <div className="ml-auto w-2.5 h-2.5 rounded-full bg-amber-400 shadow-[0_0_8px_#f59e0b] animate-pulse" />
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {/* Action */}
          <div>
            <p className="text-[10px] font-mono font-bold text-amber-500/70 uppercase tracking-widest mb-1.5">
              Requested Action
            </p>
            <p className="text-sm font-mono text-slate-300 leading-relaxed bg-dark-bg border border-dark-border rounded-lg px-4 py-3">
              {request.description}
            </p>
          </div>

          {/* Details toggle */}
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex items-center gap-1.5 text-xs font-mono text-sky-500 hover:text-sky-400 transition-colors"
          >
            <Code2 className="w-3.5 h-3.5" />
            {showDetails ? "Hide" : "Inspect"} payload
            {showDetails ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          {showDetails && (
            <pre className="text-[11px] font-mono text-emerald-400/80 bg-dark-bg border border-dark-border rounded-lg p-3 overflow-auto max-h-40 whitespace-pre-wrap break-words">
              {request.details}
            </pre>
          )}

          {/* Feedback */}
          <div>
            <label className="block text-[10px] font-mono font-bold text-slate-600 uppercase tracking-widest mb-1.5">
              Optional Instructions / Constraints
            </label>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Add context or constraints for the agent…"
              rows={3}
              className="w-full text-xs font-mono px-3 py-2.5 rounded-lg border border-dark-border bg-dark-bg text-slate-300 placeholder-slate-700 focus:outline-none focus:border-cf-orange/50 focus:shadow-[0_0_0_1px_rgba(246,130,31,0.15)] resize-none transition-all"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 px-6 py-4 bg-dark-bg/50 border-t border-dark-border">
          <button
            onClick={() => onReject(feedback || undefined)}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-red-900/50 bg-red-950/20 text-sm font-semibold text-red-400 hover:bg-red-950/40 hover:border-red-700/60 transition-all"
          >
            <XCircle className="w-4 h-4" />
            Reject
          </button>
          <button
            onClick={() => onApprove(feedback || undefined)}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-cf-orange text-white text-sm font-semibold hover:bg-cf-orange-dark transition-all hover:shadow-glow-orange"
          >
            <CheckCircle className="w-4 h-4" />
            Approve
          </button>
        </div>
      </div>
    </div>
  );
}
